import React, { useState, useEffect } from "react";
import useSWR from "swr";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Spinner, StatusBadge, KV } from "@/components/Atoms";
import { fmtDate, fmtRel } from "@/lib/format";
import {
    Pause, Play, RefreshCcw, Trash2, Sparkles, Globe2, Workflow,
    KeyRound, RotateCcw, ShieldAlert, Clock,
    Database, FileCog, Terminal, Server, HardDrive, Network,
    Activity, Settings2, Mail, Boxes, Mic, FileText, Zap, Shield,
    ServerCog, Wrench, Loader2, ShieldCheck,
} from "lucide-react";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import BackupsPanel from "@/components/BackupsPanel";
import CloudAuditLogModal from "@/components/CloudAuditLogModal";
import ExternalBackupPanel from "@/components/ExternalBackupPanel";
import LiveMetricsAutoscalingPanel from "@/components/LiveMetricsAutoscalingPanel";
import CloudCredentialsModal from "@/components/CloudCredentialsModal";
import NCAdminCredentialsModal from "@/components/NCAdminCredentialsModal";
import CloudOccPanel from "@/components/CloudOccPanel";
import CloudLogReaderPanel from "@/components/CloudLogReaderPanel";
import CloudTunablesPanel from "@/components/CloudTunablesPanel";
import CloudDomainsPanel from "@/components/CloudDomainsPanel";
import CloudAgentUpgradePanel from "@/components/CloudAgentUpgradePanel";
import CloudAiPanel from "@/components/CloudAiPanel";
import CloudMovePanel from "@/components/CloudMovePanel";
import CloudMaintenancePanel from "@/components/CloudMaintenancePanel";
import CloudDbCredentials from "@/components/CloudDbCredentials";
import { StorageQuotaEditor } from "@/components/StorageQuotaEditor";
import CloudStorageConsistencyPanel from "@/components/CloudStorageConsistencyPanel";
import CloudBimiPanel from "@/components/CloudBimiPanel";
import ArchivePanel from "@/components/ArchivePanel";
import StalwartAdminCredentialsModal from "@/components/StalwartAdminCredentialsModal";
import SouveraDevopsPanel from "@/components/SouveraDevopsPanel";
import CloudServicesPanel from "@/components/CloudServicesPanel";

const fetcher = (u) => api.get(u).then((r) => r.data);

// ── Souvera Workspace Inline Cards ──────────────────
const WS_STATUS = {
    active: { cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30", label: "ACTIVE" },
    failed: { cls: "bg-red-500/10 text-red-400 border-red-500/30", label: "FAILED" },
    provisioning: { cls: "bg-purple-500/10 text-purple-400 border-purple-500/30", label: "PROVISIONING" },
    disabled: { cls: "bg-zinc-500/10 text-zinc-400 border-zinc-600/30", label: "DISABLED" },
    off: { cls: "bg-zinc-500/10 text-zinc-500 border-zinc-600/30", label: "OFF" },
    requested: { cls: "bg-amber-500/10 text-amber-400 border-amber-500/30", label: "REQUESTED" },
};

function WorkspaceStatusCard({ cloudId, cloud, onChange, provJobId, onProvisioningOpen, isProvisioning }) {
    const { data, mutate } = useSWR(`/api/admin/clouds/${cloudId}/services/souvera-workspace`, fetcher, { revalidateOnFocus: false, revalidateOnReconnect: false });
    const [seats, setSeats] = useState("");
    const [quota, setQuota] = useState("50");
    const [busy, setBusy] = useState(null);
    const [err, setErr] = useState(null);
    useEffect(() => { setSeats(data?.souvera_user ?? ""); }, [data?.souvera_user]);
    useEffect(() => { setQuota(String(data?.mail_quota_gb ?? 50)); }, [data?.mail_quota_gb]);
    if (!data) return null;
    const status = data.status || "off";
    const st = WS_STATUS[status] || WS_STATUS.off;

    const saveSeats = async () => {
        setBusy("seats"); setErr(null);
        try { await api.post(`/api/admin/clouds/${cloudId}/services/souvera-workspace/licenses`, { seats: seats === "" ? null : Number(seats) }); await mutate(); onChange && await onChange(); }
        catch (e) { setErr(formatApiError(e)); } setBusy(null);
    };
    const saveQuota = async () => {
        setBusy("quota"); setErr(null);
        try { await api.post(`/api/admin/clouds/${cloudId}/services/souvera-workspace/mail-quota`, { quota_gb: quota === "" ? null : Number(quota) }); await mutate(); onChange && await onChange(); }
        catch (e) { setErr(formatApiError(e)); } setBusy(null);
    };

    return (
        <div className="cm-card border border-zinc-800 p-4">
            <div className="flex items-center justify-between mb-3">
                <span className="cm-eyebrow">Workspace</span>
                <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-mono border ${st.cls}`}>{st.label}</span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[11px]">
                <span className="text-zinc-500">Domain</span>
                <span className="font-mono text-zinc-300 text-right truncate">{data.mail_domain || "—"}</span>
                <span className="text-zinc-500">Stalwart IP</span>
                <span className="font-mono text-zinc-300 text-right">{data.stalwart_ip || "—"}</span>
                <span className="text-zinc-500">Users (seats)</span>
                <div className="flex items-center gap-1 justify-end">
                    <input value={seats} onChange={e => setSeats(e.target.value)} type="number" min="1"
                        className="w-14 px-1 py-0 text-[11px] font-mono bg-zinc-800 border border-zinc-700 rounded-sm text-zinc-200 text-right" />
                    <button onClick={saveSeats} disabled={busy === "seats"}
                        className="text-[10px] font-mono text-zinc-400 hover:text-zinc-200 disabled:opacity-30">✓</button>
                </div>
                <span className="text-zinc-500">Mail quota (GB)</span>
                <div className="flex items-center gap-1 justify-end">
                    <input value={quota} onChange={e => setQuota(e.target.value)} type="number" min="1"
                        className="w-14 px-1 py-0 text-[11px] font-mono bg-zinc-800 border border-zinc-700 rounded-sm text-zinc-200 text-right" />
                    <button onClick={saveQuota} disabled={busy === "quota"}
                        className="text-[10px] font-mono text-zinc-400 hover:text-zinc-200 disabled:opacity-30">✓</button>
                </div>
            </div>
            {(provJobId || isProvisioning) && (
                <button onClick={() => onProvisioningOpen(true)}
                    className="mt-2 w-full text-[10px] font-mono text-purple-400 hover:text-purple-300 border border-purple-800/50 hover:bg-purple-500/10 px-2 py-1 rounded-sm flex items-center justify-center gap-1">
                    <RefreshCcw size={9} /> Provisioning live
                </button>
            )}
            {err && <div className="mt-2 text-[10px] text-red-400 font-mono">{err}</div>}
        </div>
    );
}

function MailHealthCard({ cloudId }) {
    const { data } = useSWR(`/api/admin/clouds/${cloudId}/services/mail/health`, fetcher, { revalidateOnFocus: false, revalidateOnReconnect: false });
    if (!data) return null;
    const dot = (ok) => <span className={"w-1.5 h-1.5 rounded-full " + (ok ? "bg-emerald-400" : "bg-red-400")} />;
    const items = [
        { label: "souvera_mail", ok: data.souvera_mail?.status === "ok" },
        { label: "OIDC", ok: data.oidc?.status === "ok" },
        { label: "Stalwart", ok: data.stalwart?.status === "ok" },
        { label: "HAProxy LB", ok: data.haproxy?.status === "ok" },
    ];
    const vms = data.vms || [];
    return (
        <div className="cm-card border border-zinc-800 p-4">
            <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-400 mb-3">Mail Health</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                {items.map(i => (
                    <div key={i.label} className="flex items-center gap-1.5">
                        {dot(i.ok)}
                        <span className="text-[11px] font-mono text-zinc-400">{i.label}</span>
                    </div>
                ))}
            </div>
            {vms.length > 0 && (
                <div className="border-t border-zinc-800 pt-2">
                    <div className="text-[10px] text-zinc-500 font-mono mb-1.5">Engine-Plugins</div>
                    {vms.map(vm => (
                        <div key={vm.vmid} className="flex items-center justify-between py-0.5">
                            <span className="text-[10px] font-mono text-zinc-400">{vm.role}#{vm.vmid}</span>
                            <span className="text-[10px] font-mono">{vm.status === "ok"
                                ? <span className="text-emerald-400">✓</span>
                                : <span className="text-red-400" title={(vm.issues || []).join(", ")}>✗</span>}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function DnsRecordsCard({ cloudId }) {
    const { data, isLoading } = useSWR(`/api/admin/clouds/${cloudId}/services/souvera-workspace`, fetcher, { revalidateOnFocus: false, revalidateOnReconnect: false });
    if (isLoading) return <div className="cm-card border border-zinc-800 p-4" style={{ minHeight: 80 }}><Loader2 size={14} className="animate-spin text-zinc-500 mx-auto" /></div>;
    if (!data?.dns_records?.length) return null;
    return (
        <div className="cm-card border border-zinc-800 p-4">
            <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-400 mb-3">DNS Records ({data.dns_records.length})</div>
            <div className="space-y-2 max-h-[600px] overflow-y-auto scrollbar-none pr-1">
                {data.dns_records.map((r, i) => (
                    <div key={i} className="flex flex-col xl:flex-row xl:items-center justify-between py-1.5 border-b border-zinc-800/30 last:border-0 gap-2">
                        <div className="flex items-center gap-2 shrink-0 min-w-0">
                            <span className="text-[9px] font-mono font-bold bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded uppercase w-12 text-center select-none shrink-0">{r.type}</span>
                            <span className="text-[10px] font-mono text-zinc-300 select-all truncate max-w-[240px]" title={r.name}>{r.name}</span>
                        </div>
                        <div className="text-[10px] font-mono text-zinc-400 bg-zinc-950/60 border border-zinc-800/60 px-2.5 py-1 rounded-sm select-all whitespace-nowrap overflow-x-auto scrollbar-none max-w-full text-left xl:text-right xl:max-w-[450px]" title={r.value}>
                            {r.value}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ProvisioningModal({ cloudId, jobId, open, onClose }) {
    const [stream, setStream] = useState(null);
    useEffect(() => {
        if (!open || !jobId) { setStream(null); return; }
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const es = new EventSource(`${base}/api/admin/jobs/${jobId}/stream`, { withCredentials: true });
        const reached = new Set(); let cur = null;
        const apply = () => setStream({ reached: new Set(reached), cur });
        es.addEventListener("log", (e) => { try { const d = JSON.parse(e.data); if (d.step) { reached.add(d.step); cur = d.step; apply(); } } catch { } });
        es.addEventListener("status", (e) => { try { const s = JSON.parse(e.data); if (s.current_step) cur = s.current_step; apply(); } catch { } });
        es.addEventListener("end", () => es.close());
        es.onerror = () => es.close();
        return () => es.close();
    }, [open, jobId]);

    const STEPS = ["s3", "db", "vm", "wait", "bootstrap", "pmg", "apps", "done"];
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-lg w-[95vw] max-h-[70vh] overflow-auto bg-cm-surface border-cm-line2">
                <DialogHeader><DialogTitle className="text-cm-text font-mono text-sm">Provisioning live</DialogTitle></DialogHeader>
                <div className="space-y-1">
                    {STEPS.map(s => {
                        const done = stream?.reached?.has(s);
                        const running = stream?.cur === s && !done;
                        return (
                            <div key={s} className="flex items-center gap-2 text-[11px] font-mono">
                                <span className={done ? "text-emerald-400" : running ? "text-purple-400" : "text-zinc-600"}>
                                    {done ? "✓" : running ? "⟳" : "○"}
                                </span>
                                <span className={done || running ? "text-zinc-200" : "text-zinc-600"}>{s}</span>
                            </div>
                        );
                    })}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function WorkspaceActions({ cloudId, onChange }) {
    const [busy, setBusy] = useState(null);
    const actions = [
        ["pmg-resync", "PMG Resync"],
        ["dmarc/register", "Register DMARC"],
        ["reprovision", "Reprovision"],
        ["update-apps", "Update Apps"],
        ["harden-admins", "Harden Admins"],
        ["mail-migrate", "Migrate Mail"],
        ["postmaster-backfill", "Postmaster Backfill"],
    ];
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button className="cm-btn-secondary text-xs flex items-center gap-1.5"><Wrench size={12} /> Actions</button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
                {actions.map(([slug, label]) => (
                    <DropdownMenuItem key={slug} onClick={async () => {
                        setBusy(slug);
                        try { await api.post(`/api/admin/clouds/${cloudId}/services/souvera-workspace/${slug}`); onChange && await onChange(); }
                        catch { } setBusy(null);
                    }} disabled={busy === slug} className="cursor-pointer text-xs">
                        {busy === slug ? "..." : label}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

// ── Inline Service Toggle ─────────────────────────────
// ---- small infra cards for S3 + DB (used in Infrastruktur tab) -------------
function S3Card({ label, bucket, compact }) {
    if (!bucket) return null;
    const inner = (
        <>
            {compact && <div className="text-[11px] font-mono text-zinc-500 mb-1">{label}</div>}
            <KV k="Bucket" mono v={bucket.bucket || "—"} />
            <KV k="Endpoint" mono v={bucket.endpoint || "—"} />
            {bucket.quota_gb != null && <KV k="Quota" mono v={`${bucket.quota_gb} GB`} />}
            {bucket.secret_set !== undefined && <KV k="Key" v={bucket.secret_set ? <span className="text-emerald-400 text-[11px]">stored</span> : "—"} />}
        </>
    );
    if (compact) return <div className="space-y-0.5">{inner}</div>;
    return (
        <div className="cm-card border border-zinc-800 p-3">
            <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-400 mb-2">{label} S3</div>
            <div className="space-y-1">{inner}</div>
        </div>
    );
}

function DbCard({ label, db, cloudId, compact, stalwart }) {
    if (!db) return null;
    const inner = (
        <>
            {compact && <div className="text-[11px] font-mono text-zinc-500 mb-1">{label}</div>}
            <KV k="Database" mono v={db.db_name || "—"} />
            {db.writer_host && <KV k="Writer" mono v={`${db.writer_host}:${db.writer_port || 3306}`} />}
            <KV k="User" mono v={db.db_user || "—"} />
            <KV k="Password" v={db.db_password_set ? <span className="text-emerald-400 text-[11px]">stored</span> : "—"} />
        </>
    );
    if (compact) return <div className="space-y-0.5">{inner}</div>;
    return (
        <div className="cm-card border border-zinc-800 p-3">
            <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-400 mb-2">{label} DB</div>
            <div className="space-y-1">{inner}</div>
        </div>
    );
}

// ---- Major Upgrade button with version label ---------------------------------
function DistUpgradeBtn({ cloudId, current, disabled, action, className }) {
    const { data: up } = useSWR(
        disabled ? null : `/api/admin/clouds/${cloudId}/update-status`, fetcher,
        { refreshInterval: 0, revalidateOnFocus: false, revalidateOnReconnect: false });
    const from = up?.major || "?";
    const to = up?.next_major;
    const label = to ? `v${from} → v${to}` : "Major Upgrade";
    const btnDisabled = disabled || !to;
    return (
        <button
            onClick={(e) => {
                e.stopPropagation();
                if (!confirm(`Start Nextcloud major upgrade (${label})?\n\nReplaces worker + web VMs (cattle). NAS/DB/S3 are preserved.`)) return;
                action(`/api/admin/clouds/${cloudId}/update?target_version=${to}`);
            }}
            className={className || "cm-btn-secondary text-xs flex items-center gap-1.5"}
            disabled={btnDisabled}
            title={!to ? "No newer major versions available." : `Major upgrade to v${to} (this single step). NAS/DB/S3 are preserved.`}
        ><Sparkles size={12} /> {label}</button>
    );
}

function ServiceToggle({ icon: Icon, label, enabled, onToggle, onReconfigure, busy, testid }) {
    return (
        <div className="flex items-center justify-between py-2.5 border-b border-zinc-800/40 last:border-0">
            <div className="flex items-center gap-2.5">
                {Icon && <Icon size={14} className={enabled ? "text-emerald-400" : "text-zinc-500"} />}
                <span className="text-xs font-mono text-zinc-200">{label}</span>
            </div>
            <div className="flex items-center gap-2">
                {onReconfigure && enabled && (
                    <button onClick={onReconfigure} disabled={busy}
                        className="text-[10px] font-mono uppercase tracking-wider text-cm-muted hover:text-cm-brand px-2 py-0.5 border border-cm-line2 hover:border-cm-brand disabled:opacity-30"
                    >Reconf</button>
                )}
                <button
                    onClick={onToggle} disabled={busy}
                    data-testid={testid}
                    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-30 ${
                        enabled ? "bg-emerald-500/40" : "bg-zinc-700"
                    }`}
                >
                    <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
                        enabled ? "translate-x-[18px]" : "translate-x-[3px]"
                    }`} />
                </button>
            </div>
        </div>
    );
}

function RouteItem({ r }) {
    return (
        <div className="py-1.5 border-b border-zinc-800/30 last:border-0">
            <div className="text-[11px] font-mono text-zinc-200 truncate">{r.host || r.hostname}</div>
            <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                → {r.upstream || (r.backend_targets || []).join(", ")}
            </div>
        </div>
    );
}

function JobRow({ j }) {
    return (
        <Link to={`/jobs/${j.id}`}
            className="flex items-center justify-between py-1.5 border-b border-zinc-800/30 last:border-0 hover:bg-zinc-900/40 px-1 -mx-1 rounded-sm">
            <span className="text-[11px] font-mono text-zinc-300 truncate">{j.type}</span>
            <div className="flex items-center gap-2 shrink-0">
                <span className="text-[10px] text-zinc-600 font-mono">{fmtRel(j.created_at)}</span>
                <StatusBadge status={j.status} />
            </div>
        </Link>
    );
}

function RouteChecklist({ cloudId, primaryDomain, fallbackHostname }) {
    const { data, error, mutate } = useSWR(`/api/admin/clouds/${cloudId}/routes/check`, fetcher, { revalidateOnFocus: false, revalidateOnReconnect: false });
    const [busy, setBusy] = useState(null);
    const [msg, setMsg] = useState(null);

    const repush = async () => {
        setBusy("repush");
        setMsg(null);
        const hostname = primaryDomain || fallbackHostname;
        if (!hostname) { setMsg("No domain"); return; }
        try {
            await api.post("/api/admin/repush-route", { cloud_id: cloudId, hostname });
            await mutate();
            setMsg("Route pushed.");
        } catch (e) {
            setMsg(formatApiError(e));
        }
        setBusy(null);
    };

    if (error) return <span className="text-[10px] text-red-400 font-mono">Error: {error.message || String(error)}</span>;
    if (!data) return <span className="text-[11px] text-zinc-600 font-mono">Loading…</span>;
    const routes = data.routes || [];
    if (!routes.length) return <span className="text-[11px] text-zinc-600 font-mono">No data</span>;
    return (
        <div className="space-y-0.5">
            {routes.map((r, i) => (
                <div key={i} className="flex items-center justify-between py-0.5">
                    <span className="text-[11px] font-mono text-zinc-400">{r.label}</span>
                    <span className={`text-[10px] font-mono ${
                        r.status === "ok" ? "text-emerald-400" :
                        r.status === "missing" ? "text-red-400" : "text-zinc-500"
                    }`}>
                        {r.status === "ok" ? "✓" : r.status === "missing" ? "✗ missing" : "?"}
                    </span>
                </div>
            ))}
            <div className="pt-1 mt-1 border-t border-zinc-800 flex items-center gap-2">
                <button onClick={() => mutate()} className="text-[10px] font-mono text-zinc-500 hover:text-zinc-300 flex items-center gap-1">
                    <RefreshCcw size={9} /> Re-check
                </button>
                <button onClick={repush} disabled={busy === "repush"}
                    className="text-[10px] font-mono text-sky-400 hover:text-sky-300 border border-sky-700/50 px-1.5 py-0.5 rounded-sm disabled:opacity-40 flex items-center gap-1"
                    title="Re-push Caddy routes on LBs (incl. /push/* sub-route)">
                    <RefreshCcw size={9} className={busy === "repush" ? "animate-spin" : ""} />
                    Re-push route
                </button>
                {msg && <span className="text-[10px] text-zinc-500 font-mono">{msg}</span>}
            </div>
        </div>
    );
}

function StalwartVersionCard({ cloudId, running }) {
    const { data: check, mutate: recheck } = useSWR(
        `/api/admin/clouds/${cloudId}/archive/stalwart-check`,
        fetcher, { refreshInterval: 0, revalidateOnFocus: false, revalidateOnReconnect: false });
    const [busy, setBusy] = useState(null);
    const [msg, setMsg] = useState(null);

    const doUpdate = async () => {
        setBusy("update");
        setMsg(null);
        try {
            const r = await api.post(`/api/admin/clouds/${cloudId}/archive/update-stalwart-job`);
            setMsg(`Job ${r.data.job_id?.slice(0, 8)}... started`);
        } catch (e) {
            setMsg(formatApiError(e));
        }
        setBusy(null);
    };

    const current = check?.running || running || "?";
    const latest = check?.latest || current;
    const available = check?.update_available;

    return (
        <div>
            <div className="flex items-center justify-between gap-2">
                <div>
                    <span className={`text-[11px] font-mono ${available ? "text-amber-400" : "text-zinc-300"}`}>
                        v{current}
                    </span>
                    {available && (
                        <span className="text-[11px] font-mono text-zinc-500 ml-1">
                            → v{latest}
                        </span>
                    )}
                    {check && !available && check.latest !== current && (
                        <span className="text-[11px] font-mono text-zinc-600 ml-1">
                            (latest: v{check.latest})
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    <button onClick={() => { recheck(); setMsg("Checked."); }}
                        className="text-[10px] font-mono text-zinc-500 hover:text-zinc-300 flex items-center gap-1"
                        title="Check latest GitHub version">
                        <RefreshCcw size={9} /> Check
                    </button>
                    {available && (
                        <button onClick={doUpdate} disabled={busy === "update"}
                            className="text-[10px] font-mono text-amber-400 hover:text-amber-300 border border-amber-700/50 hover:bg-amber-500/10 px-2 py-0.5 rounded-sm disabled:opacity-40 flex items-center gap-1">
                            <RefreshCcw size={9} className={busy === "update" ? "animate-spin" : ""} />
                            {busy ? "..." : "Update"}
                        </button>
                    )}
                </div>
            </div>
            {msg && <div className="text-[10px] text-zinc-500 font-mono mt-1">{msg}</div>}
        </div>
    );
}

// ═══════════════════════════════════════════════════════
export default function CloudDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    // Persist active tab via URL hash (#system, #services, ...)
    const activeTab = location.hash?.replace("#", "") || "system";

    const { data, error, mutate } = useSWR(`/api/admin/clouds/${id}`, fetcher, { revalidateOnFocus: false });
    const { data: jobs } = useSWR(`/api/admin/jobs?cloud_id=${id}&limit=6`, fetcher, { revalidateOnFocus: false });
    const { data: audit } = useSWR(data ? `/api/admin/audit?resource_id=${id}&limit=5` : null, fetcher, { revalidateOnFocus: false });
    const { data: ai } = useSWR(`/api/admin/clouds/${id}/ai`, fetcher, { revalidateOnFocus: false });

    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [credsOpen, setCredsOpen] = useState(false);
    const [ncadminCredsOpen, setNcadminCredsOpen] = useState(false);
    const [stalwartAdminOpen, setStalwartAdminOpen] = useState(false);
    const [occOpen, setOccOpen] = useState(false);
    const [logsOpen, setLogsOpen] = useState(false);
    const [auditOpen, setAuditOpen] = useState(false);
    const [provOpen, setProvOpen] = useState(false);
    const [souveraSubTab, setSouveraSubTab] = useState("overview");

    const souveraActive = (data?.cloud || data)?.souvera_workspace_enabled || (data?.cloud || data)?.souvera_status === "active";
    const { data: pipe } = useSWR(
        (souveraActive && id) ? `/api/admin/clouds/${id}/services/souvera-workspace/pipeline` : null,
        fetcher, { refreshInterval: 0, revalidateOnFocus: false, revalidateOnReconnect: false });

    if (error) return <div className="p-8 text-red-400 text-sm font-mono">Cloud not found.</div>;
    if (!data) return <div className="p-12 flex justify-center"><Spinner /></div>;

    const c = data.cloud || data;
    const inst = data.instances || [];
    const s3 = data.s3;
    const dba = data.dba;
    const redis = data.redis;
    const domains = data.domains || [];
    const routes = data.routes || [];
    const autoscaling = data.autoscaling;
    const s3Stalwart = data.s3_stalwart;
    const dbStalwart = data.db_stalwart;
    const archiveS3 = data.archive_s3;
    const archiveDb = data.archive_db;
    const provJobId = (pipe?.steps || []).find(s => s.step === "souvera-workspace")?.job_id || null;
    const redemption = c.status === "redemption";

    const s3Used = c.s3_used_gb != null
        ? `${c.s3_used_gb.toFixed(1)} GB` : c.used_storage_gb != null
            ? `${c.used_storage_gb.toFixed(1)} GB` : "—";
    const activeVMs = inst.filter((i) => i.status === "active").length;
    const webVMs = inst.filter((i) => i.role === "web");
    const workerVMs = inst.filter((i) => i.role === "worker");
    const redemptionUntil = c.redemption_until ? fmtDate(c.redemption_until) : null;

    const opts = c.options || {};
    const svcEnabled = (name) => !!opts[name];
    const svcToggle = async (name) => {
        setBusy("svc-" + name);
        try { await api.post(`/api/admin/clouds/${id}/services/${name}/toggle`); await mutate(); }
        catch (e) { setErr(formatApiError(e)); }
        setBusy(false);
    };
    const svcReconfigure = async (serviceKey) => {
        if (!confirm(`Roll out the configuration for this service again?\n\nThe agent receives the updated config and applies it via OCC.`)) return;
        setBusy("reconf-" + serviceKey);
        try { await api.post(`/api/admin/clouds/${id}/services/${serviceKey}/reconfigure`); await mutate(); }
        catch (e) { setErr(formatApiError(e)); }
        setBusy(false);
    };
    // Service key mapping for reconfigure (frontend toggle name → backend service name)
    const _RECONF_SVC = { collabora: "collabora", high_performance_backend: "talk-hpb", notify_push: "notify-push" };
    // busy can be a string (svcToggle/svcReconfigure) or boolean (action helper)
    const isBusy = (prefix) => typeof busy === "string" && busy.startsWith(prefix);

    const action = async (url, method = "post", body) => {
        setBusy(true); setErr(null);
        try {
            if (method === "post") await api.post(url, body);
            else if (method === "delete") await api.delete(url);
            await mutate();
            // 13.09.-Fix: Bei Wartungs-/Suspend-Aktionen Feedback geben —
            // der Splash konvergiert auf den LBs (Pull, max 60s).
            if (url.includes("/maintenance/enable"))
                setErr("Wartungsmodus aktiv — Splash wird auf den LBs sichtbar (max 60s).");
            else if (url.includes("/maintenance/disable"))
                setErr("Wartungsmodus beendet — Live-Routen konvergieren (max 60s).");
            else if (url.includes("/suspend"))
                setErr("Cloud gesperrt — Lock-Splash konvergiert (max 60s).");
            else if (url.includes("/resume"))
                setErr("Cloud entsperrt — Live-Routen konvergieren (max 60s).");
        }
        catch (e) { setErr(formatApiError(e)); }
        setBusy(false);
    };

    const deleteCloud = () => action(`/api/admin/clouds/${id}`, "delete").then(() => navigate("/clouds"));
    const instantDelete = () => action(`/api/admin/clouds/${id}?instant=1`, "delete").then(() => navigate("/clouds"));
    const deleteKeepData = () => action(`/api/admin/clouds/${id}?keep_data=1`, "delete").then(() => navigate("/clouds"));
    const restoreCloud = () => action(`/api/admin/clouds/${id}/restore`);
    const isMaintenance = !!c.maintenance_mode;

    return (
        <div data-testid="cloud-detail-page" className="pb-12">
            {/* ── Header ── */}
            <PageHeader
                testid="cloud-header"
                back="/clouds"
                backLabel="Clouds"
                title={c.name || c.primary_domain || id.slice(0, 12)}
                subtitle={
                    <span className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono">{c.primary_domain || "no domain"}</span>
                        <StatusBadge status={c.status} />
                    </span>
                }
            />

            {/* ── Action Bar ── */}
            <div className="px-4 sm:px-6 pt-4 flex flex-wrap items-center gap-2">
                {/* Lifecycle */}
                <div className="flex items-center gap-1.5">
                    {c.status === "suspended" ? (
                        <button onClick={() => action(`/api/admin/clouds/${id}/resume`)}
                            className="cm-btn-primary text-xs flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white font-mono rounded-sm" disabled={busy}>
                            <Play size={12} className="text-emerald-200" /> Resume
                        </button>
                    ) : (
                        <button onClick={() => action(`/api/admin/clouds/${id}/suspend`)}
                            className="cm-btn-secondary text-xs flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-mono rounded-sm border border-zinc-700" disabled={busy}>
                            <Pause size={12} className="text-amber-400" /> Suspend
                        </button>
                    )}

                    {isMaintenance ? (
                        <button onClick={() => action(`/api/admin/clouds/${id}/maintenance/disable`)}
                            className="cm-btn-secondary text-xs flex items-center gap-1.5 px-3 py-1.5 bg-amber-950/40 text-amber-400 font-mono border border-amber-800 rounded-sm" disabled={busy} title="End maintenance mode — cloud is live again.">
                            <Wrench size={12} className="animate-pulse" /> End maintenance
                        </button>
                    ) : (
                        <button onClick={() => { if (confirm("Enable maintenance mode?\n\nAll hostnames serve a maintenance page (HTTP 503). VMs keep running.")) action(`/api/admin/clouds/${id}/maintenance/enable`); }}
                            className="cm-btn-secondary text-xs flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-mono rounded-sm border border-zinc-700" disabled={busy} title="Serve a maintenance page on all domains (503).">
                            <Wrench size={12} /> Maintenance
                        </button>
                    )}
                </div>

                <div className="w-px self-stretch bg-cm-line2 mx-1" />

                {/* Dropdowns */}
                <div className="flex items-center gap-1.5">
                    {/* Access & Credentials */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button className="cm-btn-secondary text-xs flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-mono rounded-sm border border-zinc-700" disabled={busy || c.nc_install_state !== "installed"}>
                                <KeyRound size={12} /> Access &amp; Credentials
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-56 bg-zinc-900 border border-zinc-800">
                            <DropdownMenuItem onClick={() => setCredsOpen(true)} className="cursor-pointer text-xs text-zinc-300 hover:bg-zinc-800 font-mono flex items-center gap-1.5">
                                <KeyRound size={12} /> Nextcloud Credentials
                            </DropdownMenuItem>
                            {souveraActive && (
                                <>
                                    <DropdownMenuItem onClick={() => setNcadminCredsOpen(true)} className="cursor-pointer text-xs text-zinc-300 hover:bg-zinc-800 font-mono flex items-center gap-1.5">
                                        <ShieldAlert size={12} /> NCAdmin Credentials
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setStalwartAdminOpen(true)} className="cursor-pointer text-xs text-zinc-300 hover:bg-zinc-800 font-mono flex items-center gap-1.5">
                                        <ServerCog size={12} /> Stalwart Credentials
                                    </DropdownMenuItem>
                                </>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Diagnostics */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button className="cm-btn-secondary text-xs flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-mono rounded-sm border border-zinc-700" disabled={busy || c.nc_install_state !== "installed"}>
                                <Terminal size={12} /> Diagnostics
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-56 bg-zinc-900 border border-zinc-800">
                            <DropdownMenuItem onClick={() => setOccOpen(true)} className="cursor-pointer text-xs text-zinc-300 hover:bg-zinc-800 font-mono flex items-center gap-1.5">
                                <Terminal size={12} /> OCC Console
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setLogsOpen(true)} className="cursor-pointer text-xs text-zinc-300 hover:bg-zinc-800 font-mono flex items-center gap-1.5">
                                <FileText size={12} /> Live Log Reader
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setAuditOpen(true)} className="cursor-pointer text-xs text-zinc-300 hover:bg-zinc-800 font-mono flex items-center gap-1.5" title="Central admin_audit trail of the cloud (ClickHouse).">
                                <ShieldCheck size={12} /> Central Audit Trail
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Maintenance / Ops */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button className="cm-btn-secondary text-xs flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-mono rounded-sm border border-zinc-700" disabled={busy}>
                                <FileCog size={12} /> Maintenance Tools
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-56 bg-zinc-900 border border-zinc-800 p-1 space-y-0.5">
                            <DropdownMenuItem onClick={() => { if (confirm("Repair this cloud?\n\nRebuilds worker + web VMs from the same major template. NAS/DB/S3 remain unchanged.")) action(`/api/admin/clouds/${id}/repair`); }}
                                className="cursor-pointer text-xs text-zinc-300 hover:bg-zinc-800 font-mono flex items-center gap-1.5" disabled={c.status === "suspended"}>
                                <FileCog size={12} /> Repair Cloud VMs
                            </DropdownMenuItem>
                            {c.shared_mode && (
                                <DistUpgradeBtn
                                    cloudId={id}
                                    current={c.nc_version}
                                    disabled={busy || c.nc_install_state !== "installed"}
                                    action={action}
                                    className="w-full text-left text-xs px-2 py-1.5 rounded-sm hover:bg-zinc-800 text-zinc-300 font-mono flex items-center gap-1.5 disabled:opacity-30 cursor-pointer"
                                />
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                <div className="flex-1" />

                {/* Danger zone */}
                <div className="flex items-center gap-1.5 border-l border-cm-line2 pl-2">
                    {redemption ? (
                        <>
                            <button onClick={restoreCloud}
                                className="cm-btn-primary text-xs flex items-center gap-1.5 px-3 py-1.5 bg-sky-600 hover:bg-sky-500 text-white font-mono rounded-sm" disabled={busy}><RotateCcw size={12} /> Restore</button>
                            <button onClick={instantDelete}
                                className="cm-btn-danger text-xs flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white font-mono rounded-sm" disabled={busy}><Trash2 size={12} /> Delete now</button>
                        </>
                    ) : (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className="cm-btn-danger text-xs flex items-center gap-1.5 px-3 py-1.5 bg-red-900/40 hover:bg-red-900/60 text-red-200 border border-red-800 font-mono rounded-sm" disabled={busy}><Trash2 size={12} /> Delete</button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56 bg-zinc-900 border border-zinc-800">
                                <DropdownMenuItem onClick={deleteCloud} className="cursor-pointer text-xs text-zinc-300 hover:bg-zinc-800 font-mono flex items-center gap-1.5">Trash (14 days)</DropdownMenuItem>
                                <DropdownMenuItem onClick={deleteKeepData} className="cursor-pointer text-xs text-zinc-300 hover:bg-zinc-800 font-mono flex items-center gap-1.5">Keep DB+S3</DropdownMenuItem>
                                <DropdownMenuSeparator className="bg-zinc-800" />
                                <DropdownMenuItem onClick={instantDelete} className="cursor-pointer text-xs text-red-400 hover:bg-red-950/40 hover:text-red-300 font-mono flex items-center gap-1.5">Delete immediately</DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            </div>

            {/* ── Error & Redemption ── */}
            {err && <div className="mx-4 sm:mx-6 mt-3 mb-3 p-2 border border-red-900/40 bg-red-500/5 rounded-sm text-[11px] text-red-400 font-mono">{err}</div>}
            {c.last_error && (
                <div className="mx-4 sm:mx-6 mb-3 text-[10px] text-red-400/70 font-mono flex items-center gap-2">
                    <span className="truncate">{c.last_error}</span>
                    <button onClick={() => action(`/api/admin/clouds/${id}/clear-error`)} className="text-zinc-500 hover:text-zinc-300 shrink-0">×</button>
                </div>
            )}
            {redemption && (
                <div className="mx-4 sm:mx-6 mb-3 p-3 border border-red-900/50 bg-red-950/20 rounded-sm flex items-start gap-2">
                    <ShieldAlert size={14} className="text-red-400 shrink-0 mt-0.5" />
                    <span className="text-[11px] text-red-200/80 font-mono">Deleted · VMs stopped · {redemptionUntil ? `permanent: ${redemptionUntil}` : "Redemption"}</span>
                </div>
            )}

            {/* ── Stats Row ── */}
            <div className="mx-4 sm:mx-6 mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                {[
                    ["Version", c.nc_version || "—"],
                    ["VMs", `${activeVMs}/${inst.length} aktiv`, `${webVMs.length} web · ${workerVMs.length} worker`],
                    ["Storage", `${c.storage_gb || 0} GB`, `${s3Used} belegt`],
                    [souveraActive ? "Souvera Workspace" : "Reseller", souveraActive ? "aktiv" : (c.reseller_name || c.reseller_id?.slice(0, 10) + "…" || "—")],
                ].map(([l, v, s]) => (
                    <div key={l} className="cm-card border border-zinc-800 p-3">
                        <span className="text-[9px] text-zinc-500 uppercase tracking-wide font-mono block">{l}</span>
                        <span className="text-sm font-mono text-zinc-200 mt-0.5 block">{v}</span>
                        {s && <span className="text-[9px] text-zinc-600 font-mono block mt-0.5">{s}</span>}
                    </div>
                ))}
            </div>

            {/* ═══ TABS ═══ */}
            <div className="px-4 sm:px-6">
            <Tabs value={activeTab} onValueChange={(v) => {
                window.location.hash = v;
            }}>
                <TabsList className="w-full flex justify-start gap-0 bg-transparent border-b border-cm-line rounded-none p-0 h-auto mb-5 overflow-x-auto scrollbar-none">
                    {[
                        ["system", "System", Activity],
                        ["services", "Services", Wrench],
                        ["domains", "Domains", Globe2],
                        ["network", "Network", Network],
                        ["backups", "Backups", Database],
                        ...(souveraActive ? [["souvera", "Souvera", Mail]] : []),
                        ["diagnostics", "Diagnostics", Terminal],
                        ["advanced", "Advanced", Settings2],
                    ].map(([v, l, I]) => (
                        <TabsTrigger key={v} value={v}
                            className={`text-[11px] font-mono uppercase tracking-wider px-3 py-2.5 border-b-2 transition-colors data-[state=active]:text-cm-brand data-[state=active]:border-cm-brand data-[state=active]:bg-cm-surface2/60 text-cm-muted border-transparent hover:text-cm-text hover:border-cm-line2`}
                            data-testid={`tab-${v}`}>
                            <I size={12} className="mr-1.5" />{l}
                        </TabsTrigger>
                    ))}
                </TabsList>

                {/* ── System ── */}
                <TabsContent value="system" className="space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Product & Quota */}
                        <div className="cm-card border border-zinc-800 p-4">
                            <div className="cm-eyebrow mb-3">Product &amp; Quota</div>
                            <div className="space-y-1.5">
                                <KV k="Reseller" mono v={c.reseller_name || (c.reseller_id ? c.reseller_id.slice(0, 12) + "…" : "—")} />
                                <KV k="Region" mono v={c.region || "—"} />
                                <KV k="Channel" mono v={c.update_channel || "—"} />
                                <KV k="Created" v={fmtDate(c.created_at)} />
                                <KV k="Quota" v={<span className="flex items-center gap-1.5 justify-end">
                                    <span className="font-mono text-[11px]">{c.storage_gb} GB</span>
                                    <StorageQuotaEditor cloudId={id} currentGb={c.storage_gb}
                                        usedGb={c.s3_used_gb != null ? c.s3_used_gb : (c.used_storage_gb || 0)} onSaved={mutate} />
                                </span>} />
                            </div>
                        </div>

                        {/* Storage Integrity */}
                        <div className="cm-card border border-zinc-800 p-4">
                            <div className="cm-eyebrow mb-3">Storage Integrity</div>
                            <CloudStorageConsistencyPanel cloudId={id} />
                        </div>
                    </div>

                    {/* VMs / Instances */}
                    <div className="cm-card border border-zinc-800 p-0">
                        <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-800">
                            <Server size={13} className="text-zinc-400" />
                            <span className="cm-eyebrow">VMs / Instances ({inst.length})</span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-[11px]">
                                <thead>
                                    <tr className="text-left text-zinc-500 border-b border-zinc-800">
                                        <th className="font-medium py-2 pl-4 pr-3">Rolle / VMID</th>
                                        <th className="font-medium py-2 pr-3">Node</th>
                                        <th className="font-medium py-2 pr-3">IP</th>
                                        <th className="font-medium py-2 pr-3">Ressourcen</th>
                                        <th className="font-medium py-2 pr-4 text-right">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="font-mono">
                                    {inst.map((i) => (
                                        <tr key={i.id} data-testid={`instance-${i.id}`} className="border-b border-zinc-800/30 last:border-0 hover:bg-zinc-900/30">
                                            <td className="py-1.5 pl-4 pr-3 text-zinc-200">{i.role}#{i.proxmox_vmid}</td>
                                            <td className="py-1.5 pr-3 text-zinc-500">{i.proxmox_node || "—"}</td>
                                            <td className="py-1.5 pr-3 text-zinc-400">{i.ip_internal || "—"}</td>
                                            <td className="py-1.5 pr-3 text-zinc-500">{i.cpu_cores != null ? `${i.cpu_cores}c / ${(i.ram_mb / 1024).toFixed(0)}G / ${i.disk_gb}G` : "—"}</td>
                                            <td className="py-1.5 pr-4 text-right"><StatusBadge status={i.status} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Cache & Redis */}
                    <div className="cm-card border border-zinc-800 p-4">
                        <div className="cm-eyebrow mb-2">Cache / Redis</div>
                        <span className="text-[11px] font-mono text-zinc-400">
                            {redis
                                ? (redis.host_override
                                    ? `dedicated · ${redis.host_override}:${redis.port_override || 6379}`
                                    : `pooled · prefix=${redis.prefix || "—"}`)
                                : "—"}
                        </span>
                    </div>
                </TabsContent>

                {/* ── Services ── */}
                <TabsContent value="services" className="space-y-4">
                    <div className="cm-card border border-zinc-800 p-4">
                        <div className="cm-eyebrow mb-3">
                            Core Integrations ({[
                                !souveraActive && svcEnabled("collabora"),
                                !souveraActive && svcEnabled("high_performance_backend"),
                                svcEnabled("notify_push"),
                                svcEnabled("antivirus"),
                            ].filter(Boolean).length} active)
                        </div>
                        {!souveraActive && (
                            <>
                                <ServiceToggle icon={FileText}
                                    label="Collabora Online"
                                    enabled={svcEnabled("collabora")}
                                    onToggle={() => svcToggle("collabora")}
                                    onReconfigure={_RECONF_SVC.collabora ? () => svcReconfigure(_RECONF_SVC.collabora) : undefined}
                                    busy={isBusy("svc-collabora") || isBusy("reconf-collabora")} testid="service-toggle-collabora" />
                                <ServiceToggle icon={Mic}
                                    label="Talk HPB (Signaling + TURN)"
                                    enabled={svcEnabled("high_performance_backend")}
                                    onToggle={() => svcToggle("high_performance_backend")}
                                    onReconfigure={_RECONF_SVC.high_performance_backend ? () => svcReconfigure(_RECONF_SVC.high_performance_backend) : undefined}
                                    busy={isBusy("svc-talk_hpb") || isBusy("reconf-talk-hpb")} testid="service-toggle-talk_hpb" />
                            </>
                        )}
                        <ServiceToggle icon={Zap} label="Notify Push" enabled={svcEnabled("notify_push")}
                            onToggle={() => svcToggle("notify_push")}
                            onReconfigure={_RECONF_SVC.notify_push ? () => svcReconfigure(_RECONF_SVC.notify_push) : undefined}
                            busy={isBusy("svc-notify_push") || isBusy("reconf-notify-push")} testid="service-toggle-notify_push" />
                        <ServiceToggle icon={Shield} label="Antivirus (ICAP)" enabled={svcEnabled("antivirus")}
                            onToggle={() => svcToggle("antivirus")} busy={isBusy("svc-antivirus")} testid="service-toggle-antivirus" />
                        <ServiceToggle icon={FileText} label="Euro-Office (DocumentServer)" enabled={svcEnabled("eurooffice")}
                            onToggle={async () => {
                                setBusy("svc-eurooffice");
                                try {
                                    const en = svcEnabled("eurooffice");
                                    await api.post(`/api/admin/clouds/${id}/services/eurooffice/${en ? "disable" : "enable"}`);
                                    await mutate();
                                } catch (e) { setErr(formatApiError(e)); }
                                setBusy(false);
                            }} testid="service-toggle-eurooffice"
                            hint="Deaktiviert Collabora automatisch — nur eine Office-Suite pro Cloud" />
                    </div>

                    {/* App Store Applications */}
                    <CloudServicesPanel cloudId={id} cloud={c} onChange={mutate} />

                    {/* Auto-Skalierung (VM Autoscaling) */}
                    <div className="cm-card border border-zinc-800 p-4">
                        <div className="cm-eyebrow mb-3">Auto-Skalierung (Autoscaling metrics)</div>
                        <LiveMetricsAutoscalingPanel cloudId={id} currentStage={c.autoscale_stage}
                            policy={autoscaling} ncStatus={data.nc_status} instances={inst}
                            agentsLive={data.agents_live} onChange={mutate} />
                    </div>

                    {/* AI & LiteLLM Gateway */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <CloudAiPanel cloudId={id} cloud={c} onChange={mutate} />
                        {ai ? (
                            <div className="cm-card border border-zinc-800 p-4">
                                <div className="cm-eyebrow mb-3">LiteLLM AI Gateway Quotas</div>
                                <div className="space-y-2 mt-2">
                                    <KV k="Token Key" mono v={ai.masked_token || "—"} />
                                    <KV k="Monthly Limit" mono v={ai.monthly_token_limit != null ? `${ai.monthly_token_limit.toLocaleString()} tok/mo` : "—"} />
                                    <KV k="Tokens Used" mono v={ai.usage_tokens != null ? ai.usage_tokens.toLocaleString() : "—"} />
                                </div>
                            </div>
                        ) : (
                            <div className="cm-card border border-zinc-800 p-4 flex flex-col justify-center items-center text-center">
                                <Sparkles size={20} className="text-zinc-600 mb-2 animate-pulse" />
                                <span className="text-[11px] font-mono text-zinc-500">No active LiteLLM AI Gateway metrics</span>
                            </div>
                        )}
                    </div>
                </TabsContent>

                {/* ── Domains ── */}
                <TabsContent value="domains" className="space-y-4">
                    <div className="cm-card border border-zinc-800 p-4">
                        <div className="cm-eyebrow mb-3">Domains &amp; SSL</div>
                        <CloudDomainsPanel cloudId={id} cloud={c} domains={domains} onChange={mutate} />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {routes.length > 0 && (
                            <div className="cm-card border border-zinc-800 p-4">
                                <div className="cm-eyebrow mb-3">LB Routes ({routes.length})</div>
                                {routes.map((r) => <RouteItem key={r.id} r={r} />)}
                            </div>
                        )}
                        <div className="cm-card border border-zinc-800 p-4">
                            <div className="cm-eyebrow mb-3">Caddy Route Check</div>
                            <RouteChecklist cloudId={id} primaryDomain={c.primary_domain} fallbackHostname={c.fallback_hostname} />
                        </div>
                    </div>
                </TabsContent>

                {/* ── Network ── */}
                <TabsContent value="network" className="space-y-4">
                    <div className="cm-card border border-zinc-800 p-4">
                        <div className="cm-eyebrow mb-3">SDN / VXLAN Virtual Networking</div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-2">
                            <div>
                                <span className="text-[9px] text-zinc-500 uppercase tracking-wide font-mono block">CIDR</span>
                                <span className="text-xs font-mono text-zinc-300 mt-1 block">{c.tenant_cidr || "—"}</span>
                            </div>
                            <div>
                                <span className="text-[9px] text-zinc-500 uppercase tracking-wide font-mono block">VXLAN VNI</span>
                                <span className="text-xs font-mono text-zinc-300 mt-1 block">{c.tenant_vni || "—"}</span>
                            </div>
                            <div>
                                <span className="text-[9px] text-zinc-500 uppercase tracking-wide font-mono block">VNet</span>
                                <span className="text-xs font-mono text-zinc-300 mt-1 block">{c.tenant_vnet || "—"}</span>
                            </div>
                            <div>
                                <span className="text-[9px] text-zinc-500 uppercase tracking-wide font-mono block">Gateway</span>
                                <span className="text-xs font-mono text-zinc-300 mt-1 block">{c.tenant_gateway || "—"}</span>
                            </div>
                        </div>
                    </div>
                </TabsContent>

                {/* ── Backups ── */}
                <TabsContent value="backups" className="space-y-4">
                    <BackupsPanel cloudId={id} />
                    <ExternalBackupPanel cloudId={id} />
                </TabsContent>

                {/* ── Souvera Workspace ── */}
                {souveraActive && (
                    <TabsContent value="souvera" forceMount className="space-y-4 data-[state=inactive]:hidden" style={{ minHeight: 600 }}>
                        <div className="border border-zinc-800/60 bg-zinc-950/20 p-4 rounded-md space-y-4">
                            <Tabs value={souveraSubTab} onValueChange={setSouveraSubTab} className="w-full">
                                <TabsList className="flex flex-wrap gap-1 bg-zinc-900/50 border border-zinc-800/80 p-1 rounded-md h-auto mb-5 w-fit">
                                    {[
                                        ["overview", "Overview"],
                                        ["dns", "DNS Records"],
                                        ["devops", "DevOps & Add-ons"],
                                        ["branding", "Mail Branding"],
                                        ["archive", "E-Mail Archiv"],
                                    ].map(([v, l]) => (
                                        <TabsTrigger key={v} value={v}
                                            className="text-[10px] font-mono uppercase tracking-wider px-3.5 py-1.5 rounded-sm data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-100 text-zinc-500 hover:text-zinc-300 transition-colors">
                                            {l}
                                        </TabsTrigger>
                                    ))}
                                </TabsList>

                                {/* Sub-Tab 1: Overview */}
                                <TabsContent value="overview" className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <WorkspaceStatusCard cloudId={id} cloud={c} onChange={mutate}
                                            provJobId={provJobId} onProvisioningOpen={setProvOpen} isProvisioning={!!(c.souvera_status === "provisioning")} />
                                        <MailHealthCard cloudId={id} />
                                    </div>
                                    <div className="cm-card border border-zinc-800 p-4 max-w-md">
                                        <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-400 mb-3">Workspace Operations</div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <button
                                                onClick={async () => {
                                                    setBusy("jmap-acl"); setErr(null);
                                                    try { await api.post(`/api/admin/clouds/${id}/archive/reconcile-jmap`); }
                                                    catch (e) { setErr(formatApiError(e)); }
                                                    setBusy(false);
                                                }}
                                                disabled={busy === "jmap-acl"}
                                                className="text-[10px] font-mono text-amber-400 hover:text-amber-300 disabled:opacity-40 flex items-center gap-1.5 px-2.5 py-1.5 border border-zinc-800 hover:bg-zinc-800 rounded-sm">
                                                <RefreshCcw size={10} className={busy === "jmap-acl" ? "animate-spin" : ""} />
                                                Fix JMAP permissions
                                            </button>
                                            <WorkspaceActions cloudId={id} onChange={mutate} />
                                        </div>
                                    </div>
                                </TabsContent>

                                {/* Sub-Tab 2: DNS Records */}
                                <TabsContent value="dns" className="space-y-4">
                                    <DnsRecordsCard cloudId={id} />
                                </TabsContent>

                                {/* Sub-Tab 3: DevOps & Add-ons */}
                                <TabsContent value="devops" className="space-y-4">
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                                        <div className="cm-card border border-zinc-800 p-4 lg:col-span-1">
                                            <div className="cm-eyebrow mb-3">Stalwart Mail Server</div>
                                            <StalwartVersionCard cloudId={id} running={c.stalwart_version_running} />
                                        </div>
                                        <div className="lg:col-span-2">
                                            <SouveraDevopsPanel cloudId={id} />
                                        </div>
                                    </div>
                                </TabsContent>

                                {/* Sub-Tab 4: BIMI Branding */}
                                <TabsContent value="branding" className="space-y-4">
                                    <CloudBimiPanel cloudId={id} />
                                </TabsContent>

                                {/* Sub-Tab 5: E-Mail Archive */}
                                <TabsContent value="archive" className="space-y-4">
                                    <ArchivePanel cloudId={id} />
                                </TabsContent>
                            </Tabs>
                        </div>
                    </TabsContent>
                )}

                {/* ── Diagnostics ── */}
                <TabsContent value="diagnostics" className="space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="cm-card border border-zinc-800 p-4">
                            <div className="cm-eyebrow mb-3 font-mono flex items-center gap-1.5"><Clock size={12} /> Recent Jobs ({jobs?.length || 0})</div>
                            <div className="space-y-1 mt-2">
                                {jobs?.length ? jobs.map((j) => <JobRow key={j.id} j={j} />)
                                    : <p className="text-[11px] text-zinc-600 font-mono">No jobs</p>}
                            </div>
                        </div>
                        <div className="cm-card border border-zinc-800 p-4">
                            <div className="cm-eyebrow mb-3 font-mono flex items-center gap-1.5"><ShieldCheck size={12} /> ClickHouse Audit Trail</div>
                            <div className="space-y-2 mt-2">
                                {audit?.length ? audit.map((a) => (
                                    <div key={a.id} className="py-1.5 border-b border-zinc-800/30 last:border-0">
                                        <div className="flex justify-between text-[11px]">
                                            <span className="font-mono text-zinc-300 truncate">{a.action}</span>
                                            <span className="font-mono text-zinc-600 shrink-0 ml-2">{fmtRel(a.ts)}</span>
                                        </div>
                                        <span className="text-[10px] text-zinc-500 font-mono block mt-0.5">{a.actor_type} · {a.actor_id?.slice(0, 8)}</span>
                                    </div>
                                )) : <p className="text-[11px] text-zinc-600 font-mono">—</p>}
                            </div>
                        </div>
                    </div>
                </TabsContent>

                {/* ── Advanced ── */}
                <TabsContent value="advanced" className="space-y-4">
                    {/* Object Storage & Databases info (Moved here from Infrastructure) */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="cm-card border border-zinc-800 p-4">
                            <div className="cm-eyebrow mb-3">Object Storage (S3)</div>
                            <div className="space-y-3">
                                <S3Card label="Nextcloud" bucket={s3} compact />
                                {s3Stalwart && <S3Card label="Stalwart Mail" bucket={s3Stalwart} compact />}
                                {archiveS3 && <S3Card label="Mail Archive" bucket={archiveS3} compact />}
                                {!s3 && !s3Stalwart && !archiveS3 && <span className="text-[11px] font-mono text-zinc-600">No S3 buckets</span>}
                            </div>
                        </div>
                        <div className="cm-card border border-zinc-800 p-4">
                            <div className="cm-eyebrow mb-3">Databases</div>
                            <div className="space-y-3">
                                <DbCard label="Nextcloud" db={dba} cloudId={id} compact />
                                {dbStalwart && <DbCard label="Stalwart Mail" db={dbStalwart} cloudId={id} compact stalwart />}
                                {archiveDb && <DbCard label="Mail Archive" db={archiveDb} cloudId={id} compact />}
                                {!dba && !dbStalwart && !archiveDb && <span className="text-[11px] font-mono text-zinc-600">No databases</span>}
                            </div>
                        </div>
                    </div>

                    <CloudTunablesPanel cloudId={id} cloud={c} onSaved={mutate} />
                    <CloudMaintenancePanel cloudId={id} cloud={c} onChange={mutate} />
                    <CloudAgentUpgradePanel cloudId={id} />
                    <CloudMovePanel cloudId={id} cloud={c} onMoved={mutate} />
                </TabsContent>
            </Tabs>
            </div>

            {/* ── Modals ── */}
            <ProvisioningModal cloudId={id} jobId={provJobId} open={provOpen} onClose={() => setProvOpen(false)} />
            <Dialog open={occOpen} onOpenChange={setOccOpen}>
                <DialogContent className="max-w-5xl w-[95vw] max-h-[85vh] overflow-auto bg-cm-surface border-cm-line2">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-cm-text font-mono text-sm">
                            <Terminal size={16} /> OCC Console — {c.name}
                        </DialogTitle>
                    </DialogHeader>
                    <CloudOccPanel cloudId={id} ncInstallState={c.nc_install_state} />
                </DialogContent>
            </Dialog>

            <Dialog open={logsOpen} onOpenChange={setLogsOpen}>
                <DialogContent className="max-w-5xl w-[95vw] max-h-[85vh] overflow-auto bg-cm-surface border-cm-line2">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-zinc-100 font-mono text-sm">
                            <FileText size={16} /> Logs — {c.name}
                        </DialogTitle>
                    </DialogHeader>
                    <CloudLogReaderPanel cloudId={id} ncInstallState={c.nc_install_state} defaultExpanded bare />
                </DialogContent>
            </Dialog>

            {credsOpen && <CloudCredentialsModal cloudId={id} onClose={() => setCredsOpen(false)} />}
            {ncadminCredsOpen && <NCAdminCredentialsModal cloudId={id}
                url={c.primary_domain ? `https://${c.primary_domain}` : undefined}
                onClose={() => setNcadminCredsOpen(false)} />}
            {stalwartAdminOpen && <StalwartAdminCredentialsModal cloudId={id} onClose={() => setStalwartAdminOpen(false)} />}
            {auditOpen && <CloudAuditLogModal cloudId={id} cloudName={c.name} onClose={() => setAuditOpen(false)} />}
        </div>
    );
}
